/* Pocket Factory · P1-EDGE-1, the card's real thickness (Bryton's rules 18–21: the vinyl is printed edge to edge over
   a PVC card, so only the card's thin side edge shows the PVC's color, White, or Black at +$2 a card).
   Every card the page tilts in 3D (the hero's pair, the editor's live back, the product gallery's scenes) gets a slab
   behind its face: a few card-shaped layers stepped back in depth (assets/pf-card-edge.css colors them), so a tilt
   shows the side edge in the PVC color, as the printed card has it. Nothing is printed, priced or ordered differently
   here: no price, variant or order line is read or written.
   - The face stays where and as it is, printed edge to edge: no drawn outline, and its outer shadows are cut to the
     card (clip-path: border-box). The slab's back layer casts the face's own drop shadow instead (read from the face,
     so each place keeps its shadow), so the shadow falls behind the edge, never over it. The old solid line under the
     card that stood in for its thickness is dropped. A pile of cards drawn with several such lines is left as drawn.
   - The edge's color is CSS: html[data-pf-card-color] (assets/pf-card-color.js: the "Edge color" radios), else White;
     an Edge tile (its thumbnail's data-option-field "cardColor", snippets/pf-card-color.liquid) or a card given one
     (data-pf-edge-color) shows its own.
   - A card shown flat (an option tile, the cart's picture) or too small to show a pixel of edge gets no slab.
   - Sizes come from the card itself (its box and corner radius), so every place that shapes the card its own way
     (1.586 or 1.68 wide, 5%/8% or 10px corners, a portrait card at 63%) keeps its shape.
   - Public: window.PFCardEdge.attach(card, { color }), .detach(card), .refresh() and .colorOf(card), for option tiles
     and viewers drawn later (P1-OPT-1, P1-VIEW-1); a scene or its ancestor with data-pf-edge-off is left alone, and a
     card inside data-pf-slab (an option's own card, P1-OPT-1) has its slab however small, as attach gives it.
   Without preserve-3d or clip-path: border-box the page stays exactly as it was. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else { root.PFCardEdge = api; api.start(root); }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  // CR80 is 85.6 mm wide; its PVC (0.76 mm) with a vinyl face on each side is about 0.95 mm, 1.1% of the long side.
  // Drawn at 1.6%, so the edge reads as one to two pixels at the hero's resting tilt. A card may set its own ratio
  // (--pf-edge-thickness): an Edge tile, a macro view of the edge, draws it thicker.
  const THICKNESS = 0.016;
  // Under this width (the gallery's thumbnails) a card shows less than a pixel of edge: no slab.
  const MIN_WIDTH = 96;
  // One layer every 0.8 px of thickness, 2 to 8: the band stays solid and its corners round, with no step showing.
  const STEP = 0.8, MIN_LAYERS = 2, MAX_LAYERS = 8;
  const COLORS = ['White', 'Black'];

  /** How many layers make a slab this many px thick. */
  function layerCount(px) {
    const n = Math.round((Number(px) || 0) / STEP);
    return Math.max(MIN_LAYERS, Math.min(MAX_LAYERS, n));
  }

  /** A computed box-shadow, split into the face's own (inset), its drop shadow (blurred) and solid lines under the
   *  card (no blur, straight down: the old stand-in for its thickness). Each part keeps the computed order. */
  function splitShadows(value) {
    const text = String(value || '').trim(), out = { inset: [], drop: [], line: [] };
    if (text && text !== 'none') {
      const parts = [];
      let depth = 0, from = 0;
      for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (c === '(') depth++;
        else if (c === ')') depth = Math.max(0, depth - 1);
        else if (c === ',' && depth === 0) { parts.push(text.slice(from, i)); from = i + 1; }
      }
      parts.push(text.slice(from));
      for (const raw of parts) {
        const part = raw.trim();
        if (!part) continue;
        let bare = part;
        while (/\([^()]*\)/.test(bare)) bare = bare.replace(/\([^()]*\)/g, ' ');
        const lengths = bare.split(/\s+/).filter((t) => /^-?(?:\d+\.?\d*|\.\d+)(?:px)?$/.test(t)).map(parseFloat);
        const [x = 0, y = 0, blur = 0] = lengths;
        if (/(^|\s)inset(\s|$)/.test(bare)) out.inset.push(part);
        else if (blur === 0 && x === 0 && y >= 0) out.line.push(part);
        else out.drop.push(part);
      }
    }
    return { inset: out.inset.join(', '), drop: out.drop.join(', '), line: out.line.join(', '), lines: out.line.length };
  }

  const pick = (v) => COLORS.find((c) => c.toLowerCase() === String(v || '').trim().toLowerCase()) || '';
  /** A card's own edge: one given to it (attach), or an Edge tile's (its option thumbnail, pf-card-color.liquid). */
  function ownColor(card) {
    const given = card?.getAttribute?.('data-pf-edge-color');
    if (given) return pick(given);
    return card?.getAttribute?.('data-option-field') === 'cardColor' ? pick(card.getAttribute('data-option-value')) : '';
  }
  /** 'White' or 'Black': the card's own edge (an Edge tile), else the one chosen on the page, else White. */
  function colorOf(card) {
    const doc = card?.ownerDocument || (typeof document !== 'undefined' ? document : null);
    return ownColor(card) || pick(doc?.documentElement?.getAttribute('data-pf-card-color')) || 'White';
  }

  const is3d = (transform) => typeof transform === 'string' && transform.startsWith('matrix3d(');

  /** What tilts this card in 3D: its own transform, or the nearest of its scene and that scene's three ancestors with
   *  one. chain: the elements that keep the slab's depth (preserve-3d), from the scene up to that one. */
  function tiltOf(card, win) {
    const scene = card.parentElement;
    if (!scene) return null;
    if (is3d(win.getComputedStyle(card).transform)) return { own: true, chain: [scene] };
    const chain = [];
    let node = scene;
    for (let i = 0; i < 4 && node && node.nodeType === 1 && node !== win.document.body; i++) {
      chain.push(node);
      if (is3d(win.getComputedStyle(node).transform)) return { own: false, chain };
      node = node.parentElement;
    }
    return null;
  }

  let state = null;

  /** What one card needs, read without changing the page: its slab kept (its tilt, size and looks) or dropped; null for
   *  a card left as it is (the hero reveal's copy of the old face, assets/pocket-cro.js: its slab is hidden by CSS). */
  function look(card) {
    const { win } = state, scene = card.parentElement;
    if (!scene || !scene.classList?.contains('pc-scene') || !card.isConnected) return { card, drop: true };
    if (card.closest('.pfh-ghost')) return null;
    const forced = state.forced.has(card) || !!card.closest('[data-pf-slab]'), own = ownColor(card);
    const w = card.offsetWidth, h = card.offsetHeight;
    if (!w || !h || !card.hasAttribute('data-card-render') || card.closest('[data-pf-edge-off]') || (!forced && !own && w < MIN_WIDTH)) return { card, drop: true };
    const tilt = tiltOf(card, win);
    // Its own box-shadow, as the page's styles give it: slabs() lifts the slab's override on every scene before any card
    // is read (data-pf-edge-read).
    const style = win.getComputedStyle(card), shadows = splitShadows(style.boxShadow);
    // A pile of cards drawn with several solid lines (the gallery's "stack" view) keeps its drawing.
    if (shadows.lines > 1 || (!tilt && !forced)) return { card, drop: true };
    return { card, scene, own, w, h, tilt, shadows, ratio: parseFloat(style.getPropertyValue('--pf-edge-thickness')) || THICKNESS, radius: style.borderRadius, transform: style.transform, origin: style.transformOrigin };
  }

  /** The slabs of these cards, built, measured or taken away: every card read before the page is changed, then read once
   *  more after its scenes are placed, so the page is laid out twice for them all, never once a card (MOBILE-AUDIT-1: a
   *  step change, its cards read and changed one at a time, laid the page out 100 to 330 ms at 390). The cards that have
   *  one. */
  function slabs(cards) {
    if (!state) return [];
    const doc = state.win.document;
    // 1. Read: each card, its face's own shadow with the slab's override lifted on every scene at once.
    const lifted = [];
    for (const card of cards) {
      const scene = card?.parentElement;
      if (scene?.hasAttribute?.('data-pf-edge') && !scene.hasAttribute('data-pf-edge-read')) { scene.setAttribute('data-pf-edge-read', ''); lifted.push(scene); }
    }
    let looks;
    try { looks = cards.map((card) => (card ? look(card) : null)); } finally { for (const scene of lifted) scene.removeAttribute('data-pf-edge-read'); }
    // 2. Place: the scene and what tilts it keep depth (a scene's own perspective would be a second camera on the slab);
    // a card without a slab gives its back.
    const kept = [];
    for (const it of looks) {
      if (!it) continue;
      if (it.drop) { detach(it.card); continue; }
      const { card, scene, tilt } = it, chain = tilt ? tilt.chain : [scene];
      for (const node of state.chains.get(card) || []) if (!chain.includes(node)) unmark(node, card);
      for (const node of chain) mark(node, card);
      state.chains.set(card, chain);
      const mode = tilt?.own ? 'own' : 'ancestor';
      if (scene.getAttribute('data-pf-edge') !== mode) scene.setAttribute('data-pf-edge', mode);
      kept.push(it);
    }
    // 3. Read again: each card's place in its scene, now that the scene is positioned (its data-pf-edge).
    for (const it of kept) { it.left = it.card.offsetLeft; it.top = it.card.offsetTop; }
    // 4. Draw: each slab, from what was read.
    for (const it of kept) draw(it, doc);
    return kept.map((it) => it.card);
  }

  function draw({ card, scene, own, w, h, tilt, shadows, ratio, radius, transform, origin, left, top }, doc) {
    let edge = scene.querySelector(':scope > .pf-edge');
    if (!edge) {
      edge = doc.createElement('span');
      edge.className = 'pf-edge';
      edge.setAttribute('aria-hidden', 'true');
      // First in the scene: if anything flattens the scene, the face covers the slab and the card looks as before.
      scene.prepend(edge);
    }
    state.edges.set(card, edge);
    const thick = Math.max(w, h) * ratio, n = layerCount(thick);
    if (edge.children.length !== n) {
      const layers = [];
      for (let k = 1; k <= n; k++) {
        const layer = doc.createElement('i');
        layer.className = 'pf-edge-l';
        layer.style.setProperty('--k', String(k));
        layers.push(layer);
      }
      edge.replaceChildren(...layers);
    }
    const set = (node, prop, value) => { if (node.style.getPropertyValue(prop) !== value) node.style.setProperty(prop, value); };
    set(edge, 'left', left + 'px');
    set(edge, 'top', top + 'px');
    set(edge, 'width', w + 'px');
    set(edge, 'height', h + 'px');
    set(edge, 'border-radius', radius);
    set(edge, '--pf-edge-step', (thick / n).toFixed(3) + 'px');
    if (tilt?.own) { set(edge, 'transform', transform); set(edge, 'transform-origin', origin); }
    else { edge.style.removeProperty('transform'); edge.style.removeProperty('transform-origin'); }
    if (own) { if (edge.getAttribute('data-pf-edge-color') !== own) edge.setAttribute('data-pf-edge-color', own); }
    else edge.removeAttribute('data-pf-edge-color');
    // The face keeps its inset shadows; its drop shadow is cast from the slab's back.
    set(card, '--pf-edge-face', shadows.inset || 'none');
    const rear = edge.lastElementChild;
    if (rear && rear.style.boxShadow !== shadows.drop) rear.style.boxShadow = shadows.drop;
  }

  /** The slab of one card: built, measured, or taken away. Returns whether the card has one. */
  function sync(card) {
    return !!state && slabs([card]).includes(card);
  }

  function mark(node, card) {
    let cards = state.marks.get(node);
    if (!cards) { cards = new Set(); state.marks.set(node, cards); }
    cards.add(card);
    if (!node.hasAttribute('data-pf-edge-3d')) node.setAttribute('data-pf-edge-3d', '');
  }
  function unmark(node, card) {
    const cards = state.marks.get(node);
    if (cards) cards.delete(card);
    if (!cards || !cards.size) { state.marks.delete(node); node.removeAttribute('data-pf-edge-3d'); }
  }

  /** Takes a card's slab away, its own or one copied with its scene (a thumbnail made from the hero's): the card is as
   *  it was. */
  function detach(card) {
    if (!state || !card) return;
    const scene = state.edges.get(card)?.parentElement || card.parentElement;
    const copied = scene?.classList?.contains('pc-scene') ? scene.querySelector(':scope > .pf-edge') : null;
    state.edges.get(card)?.remove();
    copied?.remove();
    state.edges.delete(card);
    for (const node of state.chains.get(card) || []) unmark(node, card);
    state.chains.delete(card);
    card.style?.removeProperty('--pf-edge-face');
    if (!scene?.removeAttribute) return;
    scene.removeAttribute('data-pf-edge');
    if (!state.marks.has(scene)) scene.removeAttribute('data-pf-edge-3d');
  }

  /** Gives a card a slab even where none would be made (a small or flat tile), in its own color if given. */
  function attach(card, { color } = {}) {
    if (!state || !card) return null;
    if (color && COLORS.includes(color)) card.setAttribute('data-pf-edge-color', color);
    state.forced.add(card);
    watch(card);
    sync(card);
    return state.edges.get(card) || null;
  }

  function watch(card) {
    if (state.seen.has(card)) return;
    state.seen.add(card);
    state.resize?.observe(card);
  }

  /** Every card on the page, measured again (and new ones found). */
  function refresh() {
    if (!state) return;
    const { doc } = state;
    // PERF-1b: the page is leaving for checkout (Buy now, assets/pocket.js): its cards stay as they are drawn.
    if (doc.documentElement.hasAttribute('data-pf-leaving')) return;
    const cards = [...doc.querySelectorAll('.pc-card')];
    for (const card of cards) watch(card);
    slabs(cards);
    // A card that left the page takes its slab with it.
    for (const card of [...state.edges.keys()]) if (!card.isConnected) detach(card);
  }

  function start(win) {
    const doc = win?.document;
    if (state || !doc) return;
    const css = win.CSS;
    if (!css?.supports?.('transform-style', 'preserve-3d') || !css.supports('clip-path', 'border-box')) return;
    state = { win, doc, edges: new Map(), chains: new Map(), marks: new Map(), forced: new WeakSet(), seen: new WeakSet(), resize: null };
    let frame = 0;
    const later = () => {
      if (frame) return;
      frame = win.requestAnimationFrame ? win.requestAnimationFrame(() => { frame = 0; refresh(); }) : (win.setTimeout(() => { frame = 0; refresh(); }, 16), 1);
    };
    // A card that changes size (a layout or orientation change, a section opening) is measured again.
    if (win.ResizeObserver) state.resize = new win.ResizeObserver(later);
    // Cards drawn later: the cart drawer's, a viewer's, a card moved into an open choice.
    if (win.MutationObserver) {
      const cardsIn = (nodes) => [...nodes].some((n) => n.nodeType === 1 && !n.classList?.contains('pf-edge') && (n.matches?.('.pc-card,.pc-scene') || n.querySelector?.('.pc-card')));
      new win.MutationObserver((records) => {
        if (records.some((r) => r.type === 'attributes' || cardsIn(r.addedNodes) || cardsIn(r.removedNodes))) later();
      }).observe(doc.documentElement, {
        childList: true, subtree: true,
        // What changes the shadow a face casts: the gallery's view, the hero's brand shadow, the card's own base and shape.
        attributes: true, attributeFilter: ['data-scene', 'data-rv-personalized', 'data-base-color', 'data-orientation'],
      });
    }
    // The design and the page's choices change what a face casts; the hero's brand shadow eases in (--pfh-shadow).
    for (const name of ['pocket:card-config', 'pocket:design-change', 'pocket:design-reset', 'change']) doc.addEventListener(name, later);
    doc.addEventListener('transitionend', (event) => { if (event.target?.matches?.('.pf-rv')) later(); });
    win.addEventListener('resize', later);
    win.addEventListener('load', later, { once: true });
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', refresh, { once: true });
    else refresh();
  }

  return { THICKNESS, MIN_WIDTH, COLORS, layerCount, splitShadows, colorOf, tiltOf, attach, detach, refresh, start };
});
